import React, {useState} from 'react';
import '../styles/pricing.css';

const Pricing = () => {
    const [isAnnual, setIsAnnual] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const plans = [
        {
            id: 1,
            name: "Starter",
            description: "Perfect for small businesses just beginning their digital marketing journey",
            monthlyPrice: 99.99,
            annualPrice: 89.99,
            features: [
                {text: "1 AI marketing intelligent assistant", included: true},
                {text: "Content creation for 2 platforms (one account each)", included: true},
                {text: "Weekly performance reports", included: true},
                {text: "Basic audience targeting", included: true},
                {text: "Email marketing automation", included: true},
                {text: "8/5 support", included: true},
                {text: "Advanced analytics dashboard", included: false},
                {text: "Custom integrations", included: false}
            ],
            popular: false,
            cta: "Get Started"
        },
        {
            id: 2,
            name: "Growth",
            description: "Ideal for growing businesses looking to expand their digital presence",
            monthlyPrice: 199.99,
            annualPrice: 179.99,
            features: [
                {text: "3 AI intelligent assistants", included: true},
                {text: "Content creation for 5 social accounts", included: true},
                {text: "Daily performance reports", included: true},
                {text: "Advanced audience targeting", included: true},
                {text: "Email & SMS marketing automation", included: true},
                {text: "A/B testing capabilities", included: true},
                {text: "24/7 priority support", included: true},
                {text: "Advanced SEO", included: true},
                {text: "Custom integrations", included: false}
            ],
            popular: true,
            cta: "Start Growing"
        },
        {
            id: 3,
            name: "Enterprise",
            description: "Comprehensive solution for established businesses with complex marketing needs",
            monthlyPrice: 399.99,
            annualPrice: 359.99,
            features: [
                {text: "Unlimited AI intelligent assistants", included: true},
                {text: "Content creation for 10 social accounts", included: true},
                {text: "Real-time performance dashboard", included: true},
                {text: "Predictive analytics", included: false},
                {text: "Full marketing automation suite (Email, SMS, WhatsApp & Voice)", included: true},
                {text: "Custom integration capabilities", included: true},
                {text: "Advanced analytics dashboard", included: true},
                {text: "24/7 VIP support", included: true},
                {text: "White-label options", included: false}
            ],
            popular: false,
            cta: "Contact Sales"
        },
        {
            id: 4,
            name: "Executive",
            description: "The boardroom's key to market dominance, powered by custom budgets and deep competitor insights",
            monthlyPrice: 'NaN',
            annualPrice: 'NaN',
            features: [
                {text: "Unlimited AI intelligent assistants", included: true},
                {text: "Content creation for unlimited social accounts", included: true},
                {text: "Real-time performance dashboard", included: true},
                {text: "Smart campaign management", included: true},
                {text: "Predictive analytics", included: true},
                {text: "Full marketing automation suite (Email, SMS, WhatsApp & Voice)", included: true},
                {text: "Custom integration capabilities", included: true},
                {text: "Advanced analytics dashboard", included: true},
                {text: "Real-time competitor analysis", included: true},
                {text: "24/7 VIP support", included: true},
                {text: "White-label options", included: true}
            ],
            popular: false,
            cta: "Get a Personalized Quote"
        }
    ];

    const togglePricing = () => {
        setIsAnnual(!isAnnual);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0
        }).format(price);
    };

    const handlePlanSelect = (planId) => {
        setSelectedPlan(planId);
        setTimeout(() => {
            document.getElementById('contact-form').scrollIntoView({behavior: 'smooth'});
        }, 10);
    };

    return (
        <section id="pricing" className="pricing-section">
            <div className="pricing-background">
                <div className="pricing-shape shape-1"></div>
                <div className="pricing-shape shape-2"></div>
            </div>

            <div className="section-header">
                <h2>Simple, Transparent <span className="highlight">Pricing</span></h2>
                <p>Choose the plan that fits your business needs</p>

                <div className="pricing-toggle">
                    <span className={!isAnnual ? 'active' : ''}>Monthly</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={isAnnual}
                            onChange={togglePricing}
                        />
                        <span className="slider round"></span>
                    </label>
                    <span className={isAnnual ? 'active' : ''}>Annual <span
                        className="save-badge">Save 10%</span></span>
                </div>
            </div>

            <div id='pricing-container' className="pricing-container">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        className={`pricing-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
                    >
                        {plan.popular && <div className="popular-badge">Most Popular</div>}

                        <h3 className="plan-name">{plan.name}</h3>
                        <p className="plan-description">{plan.description}</p>

                        <div className="plan-price">
              <span className="price">
                {plan.monthlyPrice === 'NaN' ? '' : formatPrice(isAnnual ? plan.annualPrice : plan.monthlyPrice)}
              </span>
                            <span
                                className="period">{plan.monthlyPrice === 'NaN' ? '' : '/ month' + (isAnnual ? ', billed annually' : '') + '. Excl. VAT'}</span>
                        </div>

                        {isAnnual && (
                            <div className="annual-savings">
                                {plan.monthlyPrice === 'NaN' ? 'Tailored Pricing' : `Save ${formatPrice((plan.monthlyPrice - plan.annualPrice) * 12)} annually`}
                            </div>
                        )}

                        <ul className="features-list">
                            {plan.features.map((feature, index) => (
                                <li key={index} className={!feature.included ? 'feature-disabled' : ''}>
                                    {feature.included ? (
                                        <svg className="check-icon" viewBox="0 0 24 24">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                                        </svg>
                                    ) : (
                                        <svg className="cross-icon" viewBox="0 0 24 24">
                                            <path
                                                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                        </svg>
                                    )}
                                    {feature.text}
                                </li>
                            ))}
                        </ul>

                        <button
                            className={`plan-cta ${plan.popular ? 'cta-popular' : ''}`}
                            onClick={() => handlePlanSelect(plan.id)}
                        >
                            {plan.cta}
                        </button>
                    </div>
                ))}
            </div>

            <div className="enterprise-callout">
                <div className="callout-content">
                    <h3>Need a custom solution?</h3>
                    <p>Contact our team for a tailored plan designed specifically for your business requirements.</p>
                </div>
                <button className="secondary-button"
                        onClick={() => document.getElementById('contact-form').scrollIntoView({behavior: 'smooth'})}>
                    Schedule a Consultation
                </button>
            </div>

            <div className="pricing-faq">
                <h3>Frequently Asked Questions</h3>
                <div className="faq-grid">
                    <div className="faq-item">
                        <h4>How quickly can I see results?</h4>
                        <p>Most clients begin seeing measurable improvements in their marketing metrics within the first
                            30 days, with significant ROI typically achieved within 90 days.</p>
                    </div>
                    <div className="faq-item">
                        <h4>Can I upgrade or downgrade my plan?</h4>
                        <p>Yes, you can change your plan at any time. Upgrades take effect immediately, while downgrades
                            will be applied at the start of your next billing cycle.</p>
                    </div>
                    <div className="faq-item">
                        <h4>Is there a contract or commitment?</h4>
                        <p>No long-term contracts required. Cancel monthly or annual plans anytime. Your service
                            continues, uninterrupted, until the end of your current billing cycle: whether that's your
                            month or your year.</p>
                    </div>
                    <div className="faq-item">
                        <h4>Do you offer a free trial?</h4>
                        <p>We offer a 14-day trial for new customers to experience our platform. No payment required
                            to start your trial.</p>
                    </div>
                </div>
            </div>

            <div className="support-info">
                <h3>Need immediate assistance?</h3>
                <p>
                    Lumō, our AI assistant is available 24/7 to answer your questions.
                    Simply send a WhatsApp message to +32 476 37 12 07.
                </p>
                <div className="support-highlight">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    <p>Business hours: Monday-Friday, 9AM-5PM CEST</p>
                </div>
            </div>
        </section>
    );
};

export default Pricing;